/**
 * Application entry point.
 * Bootstraps the global store, lazy-loads UI modules, and wires critical features synchronously.
 */

// Global reactive store shared across modules
import { app } from './utils/store'

// Device / browser detection (side-effect import)
import { DetectionManager } from './utils/detect'

import { Navigation, Footer } from './modules/Navigation'
import { Viewport } from './modules/Viewport'
import AsyncLoad from './classes/AsyncLoad'

class App {
    constructor() {
        // Mark app as initialized and register core DOM references on the store
        app.init = true
        app.bundle = false
        app.root = document.body
        app.viewport = document.querySelector('[data-app-viewport]')
        app.wrapper = document.querySelector('[data-wrapper]')
        app.content = document.getElementById("app-content")

        // Progressive image loading for elements with data-src + data-webp attributes
        app.images = document.querySelectorAll('[data-src][data-webp]')
        app.images = app.images.forEach((image) => {
            return new AsyncLoad({ element: image })
        })

        app.body = document.body

        this.modules()
        this.animations()
    }

    /**
     * Discovers DOM nodes and loads matching modules.
     * Critical modules are bundled inline; everything else is code-split via dynamic import.
     */
    modules = () => {
        // Loaded immediately — required for above-the-fold layout and navigation
        const critical = [
            {
                name: 'navigation',
                selector: '[data-navigation]',
                check: (element) => (element.querySelector('#menu-toggler') && element.querySelector('[data-navigation-menu]')),
                load: (element) => {
                    new Navigation()
                }
            },
            {
                name: 'viewport',
                selector: '[data-app-viewport]',
                check: (element) => element.hasAttribute('data-app-viewport') && element.querySelector('[data-wrapper-inner]'),
                load: (element) => {
                    new Viewport()
                }
            },
            {
                name: 'footer',
                selector: 'footer',
                check: (element) => element.querySelector('#footer-language-switcher'),
                load: (element) => {
                    new Footer()
                }
            }
        ]

        critical.forEach((module, index) => {
            document.querySelectorAll(module.selector).forEach((element) => {
                if (module.check(element)) {
                    module.load(element)
                }
            })
        })

        // Lazy-loaded on demand — each entry maps a selector to a webpack chunk
        const modules = [
            {
                name: 'accordions',
                selector: '.accordion-button',
                check: (element) => element.classList.contains('accordion-button'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "accordions" */
                        /* webpackPrefetch: true */
                        './classes/Accordion'
                    ).then((module) => {
                        module.AccordionManager.create(element)
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'dropdowns',
                selector: '[data-bs-toggle="dropdown"]',
                check: (element) => element.getAttribute('data-bs-toggle') === 'dropdown',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "dropdowns" */
                        /* webpackPrefetch: true */
                        './classes/Dropdown'
                    ).then((module) => {
                        module.DropdownManager.create(element)
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'tabs',
                selector: '[data-bs-toggle="tab"]',
                check: (element) => element.getAttribute('data-bs-toggle') === 'tab',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "tabs" */
                        /* webpackPrefetch: true */
                        './classes/Tabs'
                    ).then((module) => {
                        module.TabManager.create(element)
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'marquee',
                selector: '[data-slider="marquee"]',
                check: (element) => element.getAttribute('data-slider') === 'marquee',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "marquee" */
                        /* webpackPrefetch: true */
                        './modules/Marquee'
                    ).then((module) => {
                        new module.MarqueeManager({ element: element })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'modals',
                selector: '[data-modal]',
                check: (element) => element.hasAttribute('id') && element.getAttribute('id') !== '' && !element.hasAttribute('data-menu-mega'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "modals" */
                        /* webpackPrefetch: true */
                        './classes/Modal'
                    ).then((module) => {
                        // Bind open/close handlers to modal trigger buttons
                        const initModals = () => {
                            document.querySelectorAll('[data-modal-target]').forEach((entry) => {
                                // Skip buttons that were already wired up
                                if (!entry.hasAttribute('data-modal-initialized')) {
                                    module.ModalManager.toggle(entry)
                                    entry.setAttribute('data-modal-initialized', 'true')
                                }
                            })
                            document.querySelectorAll('[data-modal-close]').forEach((entry) => {
                                if (!entry.hasAttribute('data-modal-close-initialized')) {
                                    module.ModalManager.close(entry)
                                    entry.setAttribute('data-modal-close-initialized', 'true')
                                }
                            })
                        }

                        // Run on initial page load
                        initModals()

                        // Expose globally so AJAX-injected markup can re-bind modals
                        window.reinitModals = () => {
                            initModals()
                        }
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'sliders',
                selector: '[data-slider]',
                check: (element) => element.hasAttribute('data-slider'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "sliders" */
                        /* webpackPrefetch: true */
                        './modules/Slider'
                    ).then((module) => {
                        new module.SliderManager({ element: element, options: element.hasAttribute('data-options') ? JSON.parse(element.getAttribute('data-options')) : null })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'carousels',
                selector: '[data-carousel="overview"]',
                check: (element) => element.getAttribute('data-carousel') === 'overview',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "carousels" */
                        /* webpackPrefetch: true */
                        './modules/Carousel'
                    ).then((module) => {
                        new module.CarouselManager({ element: element, options: element.hasAttribute('data-options') ? JSON.parse(element.getAttribute('data-options')) : null })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'media-pinner-modal',
                selector: '[data-media-pinned]',
                check: (element) => element.hasAttribute('data-media-pinned') && element.hasAttribute('href'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "media-pinner-modal" */
                        /* webpackPrefetch: true */
                        './modules/MediaPinnerModal'
                    ).then((module) => {
                        new module.MediaPinnerModal({ element: element }).create()
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
			{
				name: 'contact-form-selector',
				selector: '[data-contact-form-selector]',
				check: (element) => element.hasAttribute('data-contact-form-selector'),
				load: async (element) => {
					await import(
						/* webpackChunkName: "contact-form-selector" */
						/* webpackPrefetch: true */
						'./modules/ContactFormSelector'
					).then((module) => {
						new module.ContactFormSelector({ element: element })
					}).catch(error => {
						console.warn('Error loading module:', error)
					})
				}
			},
			{
				name: 'catalogue-filters',
				selector: '[data-catalogue-filters]',
				check: (element) => element.hasAttribute('data-catalogue-filters'),
				load: async (element) => {
					await import(
						/* webpackChunkName: "catalogue-filters" */
						/* webpackPrefetch: true */
						'./modules/CatalogueFilters'
					).then((module) => {
						new module.CatalogueFilters({ element: element })
					}).catch(error => {
						console.warn('Error loading module:', error)
					})
				}
			}
		]

        modules.forEach((module, index) => {
            document.querySelectorAll(module.selector).forEach((element) => {
                if (module.check(element)) {
                    module.load(element)
                }
            })
        })
    }

    /**
     * Discovers animation targets and lazy-loads matching animation classes.
     */
    animations = () => {
        const animations = [
            {
                name: 'cta-fullscreen',
                selector: '.cta-fullscreen',
                check: (element) => element.classList.contains('cta-fullscreen'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "animation-cta-fullscreen" */
                        /* webpackPrefetch: true */
                        './animations/CTAFullscreen'
                    ).then((module) => {
                        console.log(element)
                        new module.default({ element })
                    }).catch(error => {
                        console.warn('Error loading animation:', error)
                    })
                }
            }
        ]

        animations.forEach((animation) => {
            document.querySelectorAll(animation.selector).forEach((element) => {
                if (animation.check(element)) {
                    animation.load(element)
                }
            })
        })
    }
}

new App()
